import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Stack,
} from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DraftPlotGroup } from '@/api/rest/resources/types/plot';

import { useCreateDraftPlotGroup, useUpdateDraftPlotGroup } from '../hooks/useDraftPlotGroupActions';
import { AutocompleteCreateField } from './AutocompleteCreateField';

type AddToGroupDialogProps = {
  open: boolean;
  organizationId: string;
  groups: DraftPlotGroup[];
  plotIds: string[];
  onClose: () => void;
  onSaved: (group: DraftPlotGroup) => void;
};

export const AddToGroupDialog = ({
  open,
  organizationId,
  groups,
  plotIds,
  onClose,
  onSaved,
}: AddToGroupDialogProps) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [error, setError] = useState(false);

  const { submit: createGroup, isSubmitting: isCreating } = useCreateDraftPlotGroup();
  const { submit: updateGroup, isSubmitting: isUpdating } = useUpdateDraftPlotGroup();
  const isPending = isCreating || isUpdating;

  const handleClose = () => {
    setSearchValue('');
    setError(false);
    onClose();
  };

  const handleSelectExisting = async (group: DraftPlotGroup) => {
    setError(false);
    try {
      const updated = await updateGroup(group.id, {
        draft_plot_ids: Array.from(new Set([...group.draft_plot_ids, ...plotIds])),
      });
      setSearchValue('');
      onSaved(updated);
    } catch {
      setError(true);
    }
  };

  const handleCreateNew = async () => {
    const name = searchValue.trim();
    if (!name) return;
    setError(false);
    try {
      const created = await createGroup({
        name,
        organization: organizationId,
        draft_plot_ids: plotIds,
      });
      setSearchValue('');
      onSaved(created);
    } catch {
      setError(true);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose();
      }}
    >
      <DialogContent>
        <DialogHeader className='typography-h2 w-full text-success md:typography-h3'>
          <DialogTitle>{t('shared.uploadPlots.addToGroupDialog.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack spacing={2}>
            <label className='typography-overline text-text-secondary'>
              {t('shared.uploadPlots.addToGroupDialog.fieldLabel')}
            </label>

            <AutocompleteCreateField
              items={groups}
              getItem={(group) => ({
                key: group.id,
                label: group.name,
                meta: t('shared.uploadPlots.addToGroupDialog.plotCount', { count: group.total_plots }),
              })}
              searchValue={searchValue}
              onSearchValueChange={setSearchValue}
              onSelectItem={handleSelectExisting}
              onCreateNew={handleCreateNew}
              disabled={isPending}
              placeholder={t('shared.uploadPlots.addToGroupDialog.placeholder')}
              noResultsLabel={t('shared.uploadPlots.addToGroupDialog.noResults')}
              createOptionLabel={(name) => t('shared.uploadPlots.createOption', { name })}
              inputClassName='w-full rounded-lg border border-neutral-black-23 px-3 py-2 text-sm outline-none focus:border-primary-100'
            />

            {error && (
              <span className='typography-caption text-error'>
                {t('shared.uploadPlots.addToGroupDialog.saveFailed')}
              </span>
            )}
          </Stack>
        </DialogBody>
        <DialogFooter className='pt-4 flex-row justify-end'>
          <Button variant='outline' onClick={handleClose}>
            {t('global.ui.buttons.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
